import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, CircularProgress, Snackbar, Alert, Grid, Breadcrumbs, Link } from '@mui/material';
import PageContainer from '../../components/container/PageContainer';
import LineNameLabel from '../../components/common/LineNameLabel';
import { useDispatch, useSelector } from 'react-redux';

import BoardCategory from '../../components/apps/kanban/BoardCategory';
import AddIcon from '@mui/icons-material/Add';
import { getBoards, togglePinnedBoard, createBoard, updateBoard } from '../../reducers/kanban/boardActions';
import { useToken } from '../../hooks/useToken';
import CreateBoardModal from '../../components/apps/kanban/CreateBoardModal';
import SelectBoardTypeModal from '../../components/apps/kanban/SelectBoardTypeModal';
import ViewBoardModal from '../../components/apps/kanban/ViewBoardModal';
import { useNavigate } from 'react-router-dom';
import BoardRouter from '../../components/apps/kanban/BoardRouter';
import { getAgentsFromApi } from '../../actions/agentes';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

/**
 * Configuración de la ruta de navegación (breadcrumb)
 * para la ubicación actual en la aplicación
 */
const BCrumb = [
  {
    title: 'Inicio',
    onClick: true
  },
  {
    title: 'Lista de Tableros',
  },
];

/**
 * Títulos legibles para las categorías de boards
 * Mapeo simple de los valores internos a títulos para mostrar
 */
const CATEGORY_TITLES = {
  GENERAL: 'General',
  SOPORTE: 'Soporte', 
  FLUJOS_BOTS: 'Flujo Bot'
};

/**
 * Componente principal para la visualización y gestión de tableros Kanban
 * Implementa el principio de responsabilidad única (SRP) de SOLID
 * gestionando exclusivamente la visualización y organización de tableros
 */
const BoardsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Obtener el token de autenticación mediante hook personalizado
  const { getTokenSilent } = useToken();

  // Obtener estados globales mediante Redux
  const agent = useSelector(state => state.agent);
  // Adaptación a la nueva estructura del estado después de la refactorización
  const {
    boards: { items: boards, loading: boardsLoading }
  } = useSelector(state => state.kanban);


  /**
   * Estados del componente organizados por funcionalidad
   */
  const [categorizedBoards, setCategorizedBoards] = useState({
    GENERAL: [],
    SOPORTE: [],
    FLUJOS_BOTS: []
  });

  // Estado para controlar la carga inicial
  const [isLoading, setIsLoading] = useState(true);

  // Estados para controlar los modales de creación
  const [selectTypeModalOpen, setSelectTypeModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedBoardType, setSelectedBoardType] = useState('');

  // Estados para el modal de detalles del tablero
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);

  // Estado para tableros fijados (favoritos)
  const [pinnedBoardsData, setPinnedBoardsData] = useState([]);

  // Estado para sistema de notificaciones
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  /**
   * Handler personalizado para navegación del breadcrumb
   * Evita conflictos con otras navegaciones programáticas
   */
  const handleBreadcrumbNavigation = (event, itemTitle) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (itemTitle === 'Inicio') {
      navigate('/admin/chat');
    }
  };

  /**
   * Alterna el estado de fijado (favorito) de un tablero
   * @param {string|number} boardId - ID del tablero a modificar
   */
  const handleTogglePinned = async (boardId) => {
    try {
      // Verificar que boardId existe
      if (!boardId) {
        showNotification('Error: ID de tablero no válido', 'error');
        return;
      }

      // Convertir boardId a string si no lo es
      const boardIdStr = String(boardId);

  

      // Obtener el token de autenticación
      const token = await getTokenSilent();
      if (!token) {
        showNotification('Error: No se pudo obtener el token de autenticación', 'error');
        return;
      }

      // Buscar el tablero para saber su estado actual de fijado
      const board = boards.find(b => String(b.id) === boardIdStr);
      if (!board) {
        showNotification('Error: No se encontró el tablero', 'error');
        return;
      }

      // Mostrar el estado actual antes de cambiarlo
      

      // togglePinnedBoard ya maneja la actualización optimista automáticamente
      try {
        await dispatch(togglePinnedBoard(boardIdStr, board.pinned, token));

        
      } catch (toggleError) {
        console.error('Error al alternar estado de fijado:', toggleError);
        showNotification('Error al alternar estado de favorito', 'error');
        return;
      }
    } catch (error) {
      console.error('Error general en handleTogglePinned:', error);
      showNotification('Error al actualizar favoritos', 'error');
    }
  };

  /**
   * Abre el modal de detalles del tablero seleccionado
   * @param {Object} board - Datos del tablero a visualizar
   */
  const handleViewBoard = (board) => {
    setSelectedBoard(board);
    setViewModalOpen(true);
  };

  /**
   * Cierra el modal de detalles del tablero
   * La limpieza del estado selectedBoard se maneja en un useEffect separado
   */
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
  };

  /**
   * Efecto para limpiar el estado del tablero seleccionado cuando se cierra el modal
   * Implementa un pequeño retraso para permitir animaciones de cierre
   */
  useEffect(() => {
    let timeoutId = null;

    if (!viewModalOpen && selectedBoard) {
      timeoutId = setTimeout(() => {
        setSelectedBoard(null);
      }, 300);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [viewModalOpen, selectedBoard]);

  /**
   * Actualiza la información de un tablero existente
   * @param {string|number} boardId - ID del tablero a actualizar
   * @param {Object} boardData - Nuevos datos del tablero
   */
  const handleUpdateBoard = async (boardId, boardData) => {
    try {
      // Verificar que hay ID de agente disponible
      if (!agent?.id && !boardData.id_agent) {
        showNotification('Error: Información de agente no disponible', 'error');
        return;
      }

      // Obtener el token de autenticación
      const token = await getTokenSilent();

      // Agregar el token a los datos del tablero si no está incluido
      const dataWithToken = { ...boardData, token };

      // Actualizar el tablero
      const result = await dispatch(updateBoard(boardId, dataWithToken));

      // Verificar si se actualizó correctamente
      if (result) {
        // Cerrar el modal de detalles sin mostrar notificación
        handleCloseViewModal();

        // Recargar los tableros para reflejar los cambios
        const id_customer = agent?.id_customer;

        if (id_customer) {
          dispatch(getBoards({
            id_customer,
            limit: 100,
            token
          }));
        }
      }
    } catch (error) {
      showNotification('Error al actualizar el tablero', 'error');
    }
  };

  /**
   * Navega a la vista detallada de un tablero específico
   * Utiliza el router centralizado para mantener la consistencia en la navegación limpia
   * @param {string|number} boardId - ID del tablero a abrir
   */
  const handleOpenBoard = (boardId) => {
    BoardRouter.navigateToBoard(boardId, navigate, dispatch);
  };

  /**
   * Efecto para cargar los tableros al montar el componente
   * Implementa el patrón de cancelación para evitar actualizaciones en componentes desmontados
   */
  useEffect(() => {
    let isMounted = true;

    const loadBoards = async () => {
      try {
        const id_customer = agent?.id_customer;

        if (!id_customer) {
          if (isMounted) setIsLoading(false);
          return;
        }

        const token = await getTokenSilent();

        if (isMounted) {
          await dispatch(getBoards({ id_customer, limit: 100, token }));
        }

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error cargando boards:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadBoards();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id_customer]);

  /**
   * Efecto para categorizar los tableros según su tipo
   * Implementa el principio de segregación de interfaces (ISP) de SOLID
   * separando la lógica de categorización de la presentación
   */
  useEffect(() => {
    // Solo actualizamos si hay tableros y no estamos cargando
    if (boards.length === 0 || isLoading) {
      return;
    }

    // Usar useCallback para memoizar la función de categorización
    const categorizeBoards = () => {
      // Organizar los tableros por categoría
      const categorized = {
        GENERAL: [],
        SOPORTE: [],
        FLUJOS_BOTS: []
      };

      boards.forEach(board => {
        // Obtener el board_type directamente - solo usar los valores reales de la BD
        const boardType = (board.board_type || '').toLowerCase().trim();
        
        // Mapeo simple basado en los valores reales que se guardan en la BD
        switch (boardType) {
          case 'general':
            categorized.GENERAL.push(board);
            break;
          case 'soporte':
            categorized.SOPORTE.push(board);
            break;
          case 'flujo_bot':
            categorized.FLUJOS_BOTS.push(board);
            break;
          default:
            // Si no es ninguno de los tipos conocidos, colocar en general como fallback
            categorized.GENERAL.push(board);
            break;
        }
      });

      return categorized;
    };

    // Calcular las nuevas categorías
    const newCategorized = categorizeBoards();

    // Solo actualizar el estado si realmente hay cambios
    // Comparación profunda para evitar actualizaciones innecesarias
    const currentStr = JSON.stringify(categorizedBoards);
    const newStr = JSON.stringify(newCategorized);

    if (currentStr !== newStr) {
      setCategorizedBoards(newCategorized);
    }
  }, [boards, isLoading, categorizedBoards]);

  /**
   * Efecto para actualizar la lista de tableros fijados (favoritos)
   * cuando cambia la lista principal de tableros
   * OPTIMIZACIÓN: Removida dependencia cíclica de pinnedBoardsData
   */
  useEffect(() => {
    // Filtrar los tableros que tienen la propiedad pinned=true
    const pinned = boards.filter(board => board.pinned === true);

    // Solo actualizar si hay cambios (sin crear dependencia cíclica)
    setPinnedBoardsData(prevPinned => {
      const prevPinnedStr = JSON.stringify(prevPinned);
      const newPinnedStr = JSON.stringify(pinned);
      
      // Solo actualizar si realmente cambió
      return prevPinnedStr !== newPinnedStr ? pinned : prevPinned;
    });
  }, [boards]);

  /**
   * Abre el modal para seleccionar el tipo de tablero a crear
   */
  const handleOpenSelectType = () => {
    setSelectTypeModalOpen(true);
  };

  /**
   * Maneja la selección del tipo de tablero y abre el modal de creación
   * @param {string} type - Tipo de tablero seleccionado
   */
  const handleSelectBoardType = (type) => {
    setSelectedBoardType(type);
    setCreateModalOpen(true);
  };

  /**
   * Cierra la notificación actual
   */
  const handleCloseNotification = () => {
    setNotification({...notification, open: false});
  };

  /**
   * Muestra una notificación al usuario
   * @param {string} message - Mensaje a mostrar
   * @param {string} severity - Tipo de notificación (info, success, warning, error)
   */
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  /**
   * Procesa la creación de un nuevo tablero
   * Implementa validaciones y manejo de errores detallado
   * @param {Object} boardData - Datos del tablero a crear
   */
  const handleSaveNewBoard = async (boardData) => {
    try {
      // Validar tipo de tablero
      if (!boardData.board_type) {
        showNotification('Error: Tipo de tablero no especificado', 'error');
        return;
      }

      // Validar información del agente
      if (!agent || !agent._id) {
        showNotification('Error: No hay información del agente', 'error');
        return;
      }

      // Normalizar el ID del creador como string
      const createdById = String(agent._id);

      // Preparar datos normalizados para la API
      const updatedBoardData = {
        ...boardData,
        created_by: createdById,
        id_agent: String(agent._id),
        // Normalizar tipo de tablero para compatibilidad con API
        board_type: boardData.board_type === 'support' ? 'soporte' : boardData.board_type
      };

      // Normalizar ID del cliente como string
      if (updatedBoardData.id_customer) {
        updatedBoardData.id_customer = String(updatedBoardData.id_customer);
      }

      // Validar ID del cliente
      if (!updatedBoardData.id_customer) {
        showNotification('Error: ID del cliente no especificado', 'error');
        return;
      }

      // Actualizar UI antes de la petición
      setCreateModalOpen(false);
      setSelectTypeModalOpen(false);
      setSelectedBoardType('');

      // Crear el tablero - La acción createBoard ya actualiza el estado de Redux
      // por lo que no es necesario llamar a getBoards después
      const result = await dispatch(createBoard(updatedBoardData));

      // Procesar resultado silenciosamente, sin mostrar notificaciones
      if (!(result && (result.id || (result.payload && result.payload.id)))) {
        // Solo mostrar notificación en caso de error
        showNotification('El tablero se creó pero hubo un problema al recuperar sus detalles', 'warning');
      }
    } catch (error) {
      // Procesar errores de forma estructurada
      let errorMessage = 'Error al crear el tablero';

      if (error.response) {
        // Personalizar mensaje según código de error HTTP
        if (error.response.status === 422) {
          errorMessage = 'Error de validación: ';

          // Extraer detalles específicos del error
          if (error.response.data.detail) {
            if (Array.isArray(error.response.data.detail)) {
              // Formatear array de errores
              const errors = error.response.data.detail.map(err =>
                `${err.loc ? err.loc.join('.').replace('query.', '') : 'Campo'}: ${err.msg}`
              ).join('; ');
              errorMessage += errors || 'Verifique los datos ingresados';
            } else if (typeof error.response.data.detail === 'string') {
              errorMessage += error.response.data.detail;
            } else if (typeof error.response.data.detail === 'object') {
              // Extraer primer mensaje de error de objeto
              const firstError = Object.values(error.response.data.detail)[0];
              errorMessage += Array.isArray(firstError) ? firstError[0] : firstError;
            }
          } else {
            errorMessage += 'Verifique los datos ingresados';
          }
        } else if (error.response.status === 401) {
          errorMessage = 'Error de autenticación: Verifique su sesión';
        } else if (error.response.status === 500) {
          errorMessage = 'Error del servidor: Intente nuevamente más tarde';
        } else {
          errorMessage = error.response.data.message || error.message || 'Error al crear el tablero';
        }
      } else {
        errorMessage = error.message || 'Error al crear el tablero';
      }

      showNotification(errorMessage, 'error');
    }
  };

  /**
   * Efecto para cargar la información del agente si no está disponible
   * Implementa el principio de inversión de dependencias (DIP) de SOLID
   * al depender de abstracciones (Redux actions) y no de implementaciones concretas
   */
  useEffect(() => {
    let isMounted = true;

    const loadAgent = async () => {
      try {
        // Si no tenemos el agente, intentamos cargarlo
        if (!agent || !agent._id) {
          const token = await getTokenSilent();
          if (!token) {
            return;
          }

          // Obtener el ID del cliente del agente actual si existe
          const id_customer = agent?.id_customer;
          if (!id_customer) {
            return;
          }

          // Cargar los agentes solo si el componente sigue montado
          if (isMounted) {
            await dispatch(getAgentsFromApi(id_customer, token));
          }
        }
      } catch (error) {
        // Error silencioso
      }
    };

    loadAgent();

    return () => {
      isMounted = false;
    };
  }, [agent, dispatch, getTokenSilent]);

  /**
   * Efecto para redirigir a /admin/chat cuando se recarga la página en /admin/kanban
   * Se ejecuta solo una vez al montar el componente
   */
  useEffect(() => {
    // Revisar si venimos de navegación normal (marcado en sessionStorage)
    const cameFromNavigation = sessionStorage.getItem('normalNavigation');
    
    if (cameFromNavigation) {
      // Si venimos de navegación normal, limpiar la marca y NO redirigir
      sessionStorage.removeItem('normalNavigation');
      return;
    }
    
    // Si no hay marca de navegación normal, verificar si es una recarga real
    const navigationType = performance.getEntriesByType('navigation')[0]?.type || 
                          (performance.navigation?.type === 1 ? 'reload' : 'navigate');
    
    if (navigationType === 'reload') {
      navigate('/admin/chat');
    }
  }, [navigate]); // navigate es estable, se ejecuta solo al montar

  /**
   * Componente breadcrumb personalizado que maneja la navegación
   * sin conflictos con otras funciones de navegación
   */
  const CustomBreadcrumb = ({ title, items }) => (
    <Grid
      container
      sx={{
        padding: '2px',
        paddingLeft: "15px",
        marginBottom: '2px'
      }}
    >
      <Grid item xs={12} sm={6} lg={8}>
        <Typography
          fontWeight="700"
          variant="h3"
          sx={{
            lineHeight: '1.235',
          }}
        >
          {title}
        </Typography>

        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          {items.map((item) => (
            <div key={item.title}>
              {item.onClick ? (
                <Link 
                  underline="none" 
                  color="inherit" 
                  component="button"
                  onClick={(e) => handleBreadcrumbNavigation(e, item.title)}
                  sx={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: 'inherit' }}
                >
                  {item.title}
                </Link>
              ) : (
                <Typography color="textPrimary">{item.title}</Typography>
              )}
            </div>
          ))}
        </Breadcrumbs>
      </Grid>
    </Grid>
  );

  return (
    <PageContainer title="Tableros Kanban" description="Lista de todos los tableros Kanban disponibles">
      <CustomBreadcrumb title={<LineNameLabel/>} items={BCrumb} />

      {/* Sección principal */}
      <Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end" mb={3}>
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenSelectType}
          >
            Nuevo Tablero
          </Button>
        </Box>

        {/* Mostrar indicador de carga si está cargando */}
        {isLoading || boardsLoading ? (
          <Box display="flex" justifyContent="center" m={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: '75vh', overflow: 'auto', pr: 1 }}>
            {/* Tableros fijados */}
            {pinnedBoardsData.length > 0 && (
              <BoardCategory
                title="Tableros Fijados"
                boards={pinnedBoardsData}
                onTogglePinned={handleTogglePinned}
                pinnedBoards={pinnedBoardsData}
                onViewDetails={handleViewBoard}
              />
            )}

            {/* Tableros por categoría */}
            {Object.keys(categorizedBoards).map(category => {
               const categoryBoards = categorizedBoards[category];
               if (categoryBoards.length === 0) return null;

               return (
                  <BoardCategory
                    key={category}
                    title={CATEGORY_TITLES[category]}
                    boards={categoryBoards}
                    onTogglePinned={handleTogglePinned}
                    pinnedBoards={pinnedBoardsData}
                    onViewDetails={handleViewBoard}
                  />
               );
             })}

             {/* Mensaje si no hay tableros */}
             {boards.length === 0 && (
               <Box textAlign="center" my={5}>
                 <Typography variant="h4" color="textSecondary" gutterBottom>
                   No hay tableros disponibles
                 </Typography>
                 <Typography variant="body1" color="textSecondary">
                   Crea un nuevo tablero para comenzar
                 </Typography>
               </Box>
             )}
           </Box>
        )}
      </Box>

      {/* Modal para seleccionar tipo de tablero */}
      <SelectBoardTypeModal
        open={selectTypeModalOpen}
        onClose={() => setSelectTypeModalOpen(false)}
        onSelectType={handleSelectBoardType}
      />

      {/* Modal para crear un nuevo tablero */}
      <CreateBoardModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleSaveNewBoard}
        boardType={selectedBoardType}
      />

      {/* Modal para ver y editar detalles de un tablero */}
      <ViewBoardModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        board={selectedBoard}
        onUpdate={handleUpdateBoard}
        onOpenBoard={handleOpenBoard}
      />

      {/* Notificaciones */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default BoardsList;